import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Icon, Input, message, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { convertImage, contentResolutionLabel, isDynamicContent } from '../../../../../services/contents';
import Colors from '../../../../../themes/Colors';
import { ACCEPT, MAX_SIZE_MB } from '../../../adLibrary/utils/fileValidation';
import useMergedDropzoneRef from '../../../adLibrary/utils/useMergedDropzoneRef';
import HideWhenCampaignActive from '../../containers/HideWhenCampaignActiveContainer';
import ContentSpecificationAlert from '../contents_of_campaign/ContentSpecificationAlert';

export const WELL_DROPPABLE_ID = 'version-ads-well';

const WellAdCard = ({
  content,
  relation,
  differentResolution,
  isOrphan,
  lastRelationScreenNames = [],
  isPaymentStatusApproved,
  readOnly,
  onPreview,
  onDeleteRequest,
  onRename,
  onCloneName,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  // Empty when there is no thumbnail and when it is still the 'pending' sentinel,
  // so neither case reaches an <img> as a broken src.
  const thumbSource = convertImage(content.file_thumb);
  const isLastRelationOfScreen = lastRelationScreenNames.length > 0;

  return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ background: Colors.snow, border: `1px solid ${Colors.border}`, borderRadius: 4, overflow: 'hidden' }}
      >
        <div style={{ padding: 8, paddingBottom: 12 }}>
          <div style={{ position: 'relative', paddingTop: '100%', background: Colors.surfaceMuted, borderRadius: 4, overflow: 'hidden' }}>
          {thumbSource && (
            <img
              src={thumbSource}
              alt={content.name}
              draggable={false}
              onClick={() => onPreview(content)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                // Dynamic ads have no resolution of their own, so their preview
                // is any shape: contain it instead of cropping it to the square.
                objectFit: isDynamicContent(content.type) ? 'contain' : 'cover',
                cursor: 'pointer',
              }}
            />
          )}
          {/* No thumbnail (a dynamic ad whose preview produced none, or one still
              transcoding): the generic globe takes the image's place. */}
          {!thumbSource && isDynamicContent(content.type) && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
            </div>
          )}
          {isOrphan && (
            <Tooltip title={t('This ad does not match any screen and will not be saved')}>
              <Icon
                type="warning"
                theme="filled"
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  color: '#f5222d',
                  background: Colors.snow,
                  borderRadius: '50%',
                  fontSize: 16,
                }}
              />
            </Tooltip>
          )}
          {differentResolution && !isOrphan && (
            <Tooltip title={t('This ad does not fit the screen resolution according to the tolerance set in the campaign')}>
              <Icon
                type="warning"
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  color: '#faad14',
                  background: Colors.snow,
                  borderRadius: '50%',
                  fontSize: 16,
                }}
              />
            </Tooltip>
          )}
          {relation && (
            <span style={{ position: 'absolute', bottom: 4, right: 28 }}>
              <ContentSpecificationAlert
                listResolutionWithContent={[relation]}
                styleCustom={{ fontSize: 16, color: '#f44336' }}
              />
            </span>
          )}
          <span
            style={{
              position: 'absolute',
              bottom: 4,
              left: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(0,0,0,0.6)',
              color: Colors.white,
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 3,
            }}
          >
            <Icon type="desktop" theme="outlined" />
            {contentResolutionLabel(content, t)}
          </span>
          <HideWhenCampaignActive
            contentId={content.id}
            displaysId={relation ? relation.displays.map((d) => d.id) : []}
          >
            {(!isPaymentStatusApproved || isOrphan) && !readOnly && isHovered && (
              isLastRelationOfScreen ? (
                <Tooltip title={t('It\'s the last ad on screen {{screenName}}; to delete it, first add another ad there', { screenName: lastRelationScreenNames.join(', ') })}>
                  <Icon
                    type="delete"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: Colors.textMuted,
                      background: Colors.snow,
                      borderRadius: '50%',
                      padding: 4,
                      cursor: 'not-allowed',
                      fontSize: 16,
                    }}
                  />
                </Tooltip>
              ) : (
                <Icon
                  type="delete"
                  onClick={() => onDeleteRequest(content)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: Colors.textSecondary,
                    background: Colors.snow,
                    borderRadius: '50%',
                    padding: 4,
                    cursor: 'pointer',
                    fontSize: 16,
                  }}
                />
              )
            )}
          </HideWhenCampaignActive>
        </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px 10px', gap: 6, background: Colors.snow }}>
          <Input
            size="small"
            placeholder={t('Identify your ad...')}
            value={content.name}
            disabled={readOnly}
            onChange={(e) => onRename(e.target.value, content)}
            style={{ border: 'none', boxShadow: 'none', padding: 0, background: 'transparent', fontSize: 14, fontWeight: 500, color: Colors.textStrong }}
          />
          {!readOnly && isHovered && (
            <Tooltip title={t('Copy the name of this ad to all ads in the same version.')}>
              <Icon type="copy" onClick={() => onCloneName(content)} style={{ cursor: 'pointer', color: Colors.textMuted, flexShrink: 0 }} />
            </Tooltip>
          )}
        </div>
      </div>
  );
};

const VersionAdsWellComponent = ({
  heading,
  contents = [],
  onRename,
  onCloneName,
  onPreview,
  onDeleteRequest,
  listResolution = [],
  isPaymentStatusApproved,
  customContents = [],
  readOnly = false,
  onUploadFiles,
  uploading = false,
  orphanContentIds,
  isTheLastOfTheRelationship,
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: WELL_DROPPABLE_ID, disabled: readOnly });

  const { rootProps: dropzoneRootProps, mergedRef, isDragActive } = useMergedDropzoneRef(
    {
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: ACCEPT,
      maxSize: MAX_SIZE_MB * 1024 * 1024,
      disabled: !onUploadFiles || uploading || readOnly,
      onDrop: (accepted, rejections) => {
        if (rejections.length) message.error(t('Supports JPG, PNG, MP4, MP3 (Max 500MB)'));
        if (accepted.length) onUploadFiles(accepted);
      },
    },
    setNodeRef
  );
  const isDropTarget = isOver || isDragActive;

  const findRelation = (content) =>
    listResolution.find((r) => (r.contents || []).some((c) => c.id === content.id));

  const wellStyle = {
    borderRadius: 4,
    border: isDropTarget ? '1px dashed #1890ff' : `1px dashed ${Colors.border}`,
    background: isDropTarget ? Colors.tintInfo : Colors.surfaceMuted,
    transition: 'background-color 120ms ease, border-color 120ms ease',
    padding: 20,
  };

  return (
    <>
      {heading && (
        <div style={{ fontWeight: 600, fontSize: 14, color: Colors.textStrong, marginBottom: 12 }}>{heading}</div>
      )}
      <div ref={mergedRef} {...dropzoneRootProps} style={wellStyle}>
        {!contents.length ? (
          <div style={{ textAlign: 'center', color: Colors.textMuted, padding: '24px 0' }}>
            <Icon type={uploading ? 'loading' : 'download'} style={{ fontSize: 24, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
            {t('Drag the ads you want to add to the campaign here, or add them from the ads list')}
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
          {contents.map((content) => {
            const relation = findRelation(content);
            const relationContent = relation && relation.contents
              ? relation.contents.find((c) => c.id === content.id)
              : null;
            const differentResolution = (relationContent || content).differentResolution;
            const isOrphan = !!(orphanContentIds && orphanContentIds.has(content.id));
            const lastRelationScreenNames =
              !isOrphan && relation && isTheLastOfTheRelationship
                ? isTheLastOfTheRelationship(relation, relationContent || content)
                : [];

            return (
              <WellAdCard
                key={content.id}
                content={content}
                relation={relation}
                differentResolution={differentResolution}
                isOrphan={isOrphan}
                lastRelationScreenNames={lastRelationScreenNames}
                isPaymentStatusApproved={isPaymentStatusApproved}
                readOnly={readOnly}
                onPreview={() => onPreview(relationContent || content, relation)}
                onDeleteRequest={onDeleteRequest}
                onRename={onRename}
                onCloneName={onCloneName}
              />
            );
          })}
        </div>
        )}
      </div>
    </>
  );
};

export default VersionAdsWellComponent;
